import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Building2, MapPin, Save, Hash, Send, GitMerge } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { isAdminRole } from "@/lib/permissions";
import { DocTypesSettingsPanel } from "@/components/DocTypeManager";

const emptyAddress = () => ({
  id: crypto.randomUUID(),
  label: "",
  address: "",
  gstin: "",
  is_default_bill_to: false,
  is_default_ship_to: false,
});

export default function CompanySettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  // Read-only asset sequence counter (fetched from the admin endpoint)
  const [assetSeq, setAssetSeq] = useState(null);

  useEffect(() => {
    if (user && !isAdminRole(user.role)) {
      toast.error("Only admins can access Company Settings.");
      navigate("/");
      return;
    }
    api.get("/company-settings").then(({ data }) => setSettings(data)).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load company settings");
    });
    api.get("/admin/asset-seq").then(({ data }) => setAssetSeq(data)).catch(() => {
      // Silently ignore — non-admin fallback (route is admin-gated but page is admin only anyway)
    });
  }, [user, navigate]);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setAddr = (idx, k, v) => setSettings((s) => ({
    ...s,
    addresses: s.addresses.map((a, i) => i === idx ? { ...a, [k]: v } : a),
  }));
  const setDefaultBill = (idx) => setSettings((s) => ({
    ...s,
    addresses: s.addresses.map((a, i) => ({ ...a, is_default_bill_to: i === idx })),
  }));
  const setDefaultShip = (idx) => setSettings((s) => ({
    ...s,
    addresses: s.addresses.map((a, i) => ({ ...a, is_default_ship_to: i === idx })),
  }));
  const addAddress = () => setSettings((s) => ({ ...s, addresses: [...(s.addresses || []), emptyAddress()] }));
  const removeAddress = (idx) => setSettings((s) => ({
    ...s,
    addresses: s.addresses.filter((_, i) => i !== idx),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        legal_name: (settings.legal_name || "").trim() || "BBS GLOBAL PVT. LTD.",
        cin: settings.cin || null,
        pan: settings.pan || null,
        gstin: settings.gstin || null,
        iec: settings.iec || null,
        msme: settings.msme || null,
        phone: settings.phone || null,
        email: settings.email || null,
        website: settings.website || null,
        addresses: (settings.addresses || []).filter((a) => a.label?.trim() && a.address?.trim()),
        digest_alerts_enabled: settings.digest_alerts_enabled === false ? false : true,
      };
      const { data } = await api.patch("/company-settings", payload);
      setSettings(data.settings);
      toast.success("Company settings saved. New POs will use these details.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-slate-400 text-center py-12">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="company-settings-page">
      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Configuration</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Company Settings</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Legal identity + address book used on every PO PDF (letterhead, Bill-To, Ship-To).
        </p>
      </div>

      {/* Legal identity */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#7C1D3A]" /> Legal Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
          <div className="md:col-span-2">
            <Label>Legal Name *</Label>
            <Input value={settings.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} data-testid="cs-legal-name" />
          </div>
          <div><Label>GSTIN</Label><Input value={settings.gstin || ""} onChange={(e) => set("gstin", e.target.value)} placeholder="e.g. 19AABCB1234K1Z5" data-testid="cs-gstin" /></div>
          <div><Label>CIN</Label><Input value={settings.cin || ""} onChange={(e) => set("cin", e.target.value)} placeholder="e.g. U29308WB1998PTC088888" data-testid="cs-cin" /></div>
          <div><Label>PAN</Label><Input value={settings.pan || ""} onChange={(e) => set("pan", e.target.value)} placeholder="e.g. AABCB1234K" data-testid="cs-pan" /></div>
          <div><Label>IEC (Imports)</Label><Input value={settings.iec || ""} onChange={(e) => set("iec", e.target.value)} placeholder="10-digit IEC" data-testid="cs-iec" /></div>
          <div><Label>UDYAM / MSME</Label><Input value={settings.msme || ""} onChange={(e) => set("msme", e.target.value)} data-testid="cs-msme" /></div>
          <div><Label>Phone</Label><Input value={settings.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="+91 33 XXXX XXXX" data-testid="cs-phone" /></div>
          <div><Label>Email</Label><Input value={settings.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="info@bbsglobal.in" data-testid="cs-email" /></div>
          <div><Label>Website</Label><Input value={settings.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="www.bbsglobal.in" data-testid="cs-website" /></div>
          <div className="md:col-span-2 pt-2 border-t border-slate-100">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                data-testid="cs-digest-toggle"
                checked={settings.digest_alerts_enabled !== false}
                onChange={(e) => set("digest_alerts_enabled", e.target.checked)}
                className="mt-1 accent-[#7C1D3A] w-4 h-4"
              />
              <div>
                <div className="text-sm font-medium text-slate-700">Batch same-day alerts into digest emails</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  When ON, the scheduler groups all matching maintenance / warranty / AMC alerts for a day into a single summary email per recipient (with a table) instead of one email per item. Recommended.
                </div>
              </div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Address book */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#7C1D3A]" /> Address Book
          </CardTitle>
          <Button size="sm" variant="outline" onClick={addAddress} data-testid="cs-add-address">
            <Plus className="w-4 h-4 mr-1" /> Add Address
          </Button>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <p className="text-xs text-slate-500">
            Every PO will let the raiser pick one Bill-To and one Ship-To from this list. Mark one default for each.
          </p>
          {(settings.addresses || []).map((a, idx) => (
            <div key={a.id || idx} className="border border-slate-200 rounded-md p-4 space-y-3 bg-slate-50" data-testid={`cs-address-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Label *</Label>
                  <Input value={a.label} onChange={(e) => setAddr(idx, "label", e.target.value)} placeholder="e.g. Registered Office" />
                </div>
                <div>
                  <Label>GSTIN (this premise)</Label>
                  <Input value={a.gstin || ""} onChange={(e) => setAddr(idx, "gstin", e.target.value)} placeholder="Optional" />
                </div>
                <div className="flex items-end justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => removeAddress(idx)}
                    className="text-red-500 hover:text-red-700 p-2 text-xs inline-flex items-center gap-1"
                    disabled={settings.addresses.length === 1}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
              </div>
              <div>
                <Label>Full Address *</Label>
                <Textarea rows={2} value={a.address} onChange={(e) => setAddr(idx, "address", e.target.value)} placeholder="Building, street, city, PIN, state, country" />
              </div>
              <div className="flex flex-wrap items-center gap-4 text-xs">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="default-bill-to"
                    checked={!!a.is_default_bill_to}
                    onChange={() => setDefaultBill(idx)}
                    className="accent-[#7C1D3A]"
                    data-testid={`cs-bill-default-${idx}`}
                  />
                  <span>Default Bill-To</span>
                  {a.is_default_bill_to && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>}
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="default-ship-to"
                    checked={!!a.is_default_ship_to}
                    onChange={() => setDefaultShip(idx)}
                    className="accent-[#7C1D3A]"
                    data-testid={`cs-ship-default-${idx}`}
                  />
                  <span>Default Ship-To</span>
                  {a.is_default_ship_to && <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Active</Badge>}
                </label>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Asset Registry sequence (read-only diagnostic) */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Hash className="w-4 h-4 text-[#7C1D3A]" /> Asset Registry Sequence
          </CardTitle>
        </CardHeader>
        <CardContent>
          {assetSeq === null ? (
            <div className="text-slate-400 text-sm">Loading counter…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Last used sequence</div>
                <div className="mt-1 font-mono text-2xl font-bold text-slate-900" data-testid="asset-seq-counter">
                  {String(assetSeq.counter_value || 0).padStart(5, "0")}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">Global counter value</div>
              </div>
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Next asset will get</div>
                <div className="mt-1 font-mono text-2xl font-bold text-emerald-800" data-testid="asset-seq-next">
                  {String(assetSeq.next_asset_seq || 1).padStart(5, "0")}
                </div>
                <div className="text-[11px] text-emerald-700 mt-1">Assigned automatically on next asset creation</div>
              </div>
              <div className="rounded-md border border-slate-200 bg-white p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Highest existing seq</div>
                <div className="mt-1 font-mono text-2xl font-bold text-slate-700" data-testid="asset-seq-max">
                  {String(assetSeq.max_existing_asset_seq || 0).padStart(5, "0")}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {(assetSeq.counter_value || 0) === (assetSeq.max_existing_asset_seq || 0)
                    ? "In sync ✓"
                    : `Counter ${(assetSeq.counter_value || 0) > (assetSeq.max_existing_asset_seq || 0) ? "ahead" : "behind"} of registry`}
                </div>
              </div>
            </div>
          )}
          <div className="text-[11px] text-slate-500 mt-3">
            Read-only view of the global sequence used when generating Asset IDs
            (format <span className="font-mono">PREFIX-NAME-<b>NNNNN</b></span>). This
            auto-resyncs whenever a PO posting is rolled back so gaps don't accrue.
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 sticky bottom-4 bg-white/80 backdrop-blur p-3 rounded-md border border-slate-200">
        <Button variant="outline" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
        <Button
          className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
          disabled={saving}
          onClick={save}
          data-testid="cs-save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Company Settings"}
        </Button>
      </div>

      {/* Deployment masters — Lines & Departments */}
      <DeploymentMasters />
      <DocTypesSettingsPanel />
    </div>
  );
}

function DeploymentMasters() {
  const [lines, setLines] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [newLine, setNewLine] = useState({ name: "", location: "" });
  const [newDept, setNewDept] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [ls, ds, locs, orph] = await Promise.all([
      api.get("/deployment-masters/lines"),
      api.get("/deployment-masters/departments"),
      api.get("/masters/locations"),
      api.get("/masters/locations/orphans").catch(() => ({ data: { orphans: [] } })),
    ]);
    setLines(ls.data || []);
    setDepartments(ds.data || []);
    setLocations(locs.data || []);
    setOrphans(orph.data?.orphans || []);
  };
  const [orphans, setOrphans] = useState([]);
  useEffect(() => { load(); }, []);

  const addLocation = async () => {
    if (!newLocation.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/masters/locations", { name: newLocation.trim() });
      setNewLocation("");
      toast.success("Location added");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const removeLocation = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"? This is blocked if assets are still stationed here.`)) return;
    try { await api.delete(`/masters/locations/${id}`); toast.success("Location removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  // Merge legacy location → active target
  //   `mergeSrc` is either:
  //     - {id, name}  (a real master row)                  → uses /{id}/merge
  //     - {orphan: true, name}  (an orphan string on assets) → uses /merge-orphan
  const [mergeSrc, setMergeSrc] = useState(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const [merging, setMerging] = useState(false);
  const openMerge = (loc) => { setMergeSrc(loc); setMergeTarget(""); };
  const runMerge = async () => {
    if (!mergeSrc || !mergeTarget) return;
    setMerging(true);
    try {
      const r = mergeSrc.orphan
        ? await api.post(`/masters/locations/merge-orphan`, { source_name: mergeSrc.name, target_name: mergeTarget })
        : await api.post(`/masters/locations/${mergeSrc.id}/merge`, { target_name: mergeTarget });
      toast.success(
        `Merged "${r.data.source}" → "${r.data.target}" · ${r.data.assets_updated} asset(s), ${r.data.lines_updated} line(s) moved`
      );
      setMergeSrc(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Merge failed");
    } finally { setMerging(false); }
  };

  const addLine = async () => {
    if (!newLine.name.trim() || !newLine.location.trim()) { toast.error("Name and location required"); return; }
    setBusy(true);
    try {
      await api.post("/deployment-masters/lines", newLine);
      setNewLine({ name: "", location: newLine.location });
      toast.success("Line added");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const removeLine = async (id) => {
    if (!window.confirm("Deactivate this line? Assets already deployed to it keep their history.")) return;
    try { await api.delete(`/deployment-masters/lines/${id}`); load(); toast.success("Line removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const addDept = async () => {
    if (!newDept.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/deployment-masters/departments", { name: newDept.trim() });
      setNewDept("");
      toast.success("Department added");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const removeDept = async (id) => {
    if (!window.confirm("Deactivate this department? Assets already assigned keep their history.")) return;
    try { await api.delete(`/deployment-masters/departments/${id}`); load(); toast.success("Department removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <Card data-testid="deployment-masters-card">
      <CardHeader>
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Send className="w-4 h-4 text-[#7C1D3A]" /> Deployment & Location Masters
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
        {/* Locations */}
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Locations</div>
          <div className="text-xs text-slate-500 mb-3">
            Top-level physical Locations that an asset can be stationed at (e.g. <i>Head Office</i>, <i>Factory</i>).
          </div>
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="Location name"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              data-testid="new-location-name"
              className="flex-1"
            />
            <Button onClick={addLocation} disabled={busy} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-location-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {locations.length === 0 ? (
            <div className="text-xs text-slate-400 py-4 border border-dashed rounded-md text-center">No locations defined yet</div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="locations-list">
              {locations.map((l) => (
                <div key={l.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-medium">{l.name}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openMerge(l)}
                      className="p-1 rounded text-slate-400 hover:text-[#7C1D3A] hover:bg-slate-50"
                      title="Merge into another location (renames all assets)"
                      data-testid={`merge-location-${l.id}`}
                    >
                      <GitMerge className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeLocation(l.id, l.name)}
                      className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-slate-50"
                      title="Deactivate location"
                      data-testid={`remove-location-${l.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Orphan location cleanup */}
          {orphans.length > 0 && (
            <div className="mt-4" data-testid="orphan-locations-block">
              <div className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
                <GitMerge className="w-3 h-3" /> Legacy locations found on {orphans.reduce((s, o) => s + o.asset_count, 0)} asset(s)
              </div>
              <div className="text-[11px] text-slate-500 mb-2 leading-snug">
                These location strings exist on assets or deployment lines but aren&apos;t registered as active Locations
                (usually left over from a CSV import). Merge each one into the correct Location to clean the dropdown.
              </div>
              <div className="divide-y divide-slate-100 border border-amber-200 bg-amber-50/40 rounded-md">
                {orphans.map((o) => (
                  <div key={o.name} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`orphan-loc-${o.name}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{o.name}</div>
                      <div className="text-[10px] text-slate-500">
                        {o.asset_count} asset{o.asset_count !== 1 ? "s" : ""}
                        {o.line_count > 0 ? ` · ${o.line_count} line${o.line_count !== 1 ? "s" : ""}` : ""}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openMerge({ orphan: true, name: o.name })}
                      className="h-7 text-xs"
                      data-testid={`merge-orphan-${o.name}`}
                    >
                      <GitMerge className="w-3 h-3 mr-1" /> Merge
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lines */}
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Lines (per Location)</div>
          <div className="text-xs text-slate-500 mb-3">
            Structured sub-locations that assets can be deployed to (e.g. <i>Sewing Line 1</i>, <i>Cutting Table A</i>). Tied to a top-level location.
          </div>
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="Line name (e.g. Sewing Line 1)"
              value={newLine.name}
              onChange={(e) => setNewLine((s) => ({ ...s, name: e.target.value }))}
              data-testid="new-line-name"
              className="flex-1"
            />
            <select
              value={newLine.location}
              onChange={(e) => setNewLine((s) => ({ ...s, location: e.target.value }))}
              data-testid="new-line-location"
              className="w-40 border border-slate-200 rounded-md px-2 text-sm h-9 bg-white"
            >
              <option value="">Location…</option>
              {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
            </select>
            <Button onClick={addLine} disabled={busy} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-line-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {lines.length === 0 ? (
            <div className="text-xs text-slate-400 py-4 border border-dashed rounded-md text-center">No lines defined yet</div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="lines-list">
              {lines.map((l) => (
                <div key={l.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium">{l.name}</span>
                    <span className="text-xs text-slate-500 ml-2">{l.location}</span>
                  </div>
                  <button onClick={() => removeLine(l.id)} className="text-slate-400 hover:text-red-600" data-testid={`remove-line-${l.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Departments */}
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Departments</div>
          <div className="text-xs text-slate-500 mb-3">
            Cost-centre / functional groups an asset can be deployed to (e.g. <i>Production</i>, <i>Maintenance</i>).
          </div>
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="Department name"
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              data-testid="new-dept-name"
              className="flex-1"
            />
            <Button onClick={addDept} disabled={busy} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-dept-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {departments.length === 0 ? (
            <div className="text-xs text-slate-400 py-4 border border-dashed rounded-md text-center">No departments defined yet</div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="departments-list">
              {departments.map((d) => (
                <div key={d.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-medium">{d.name}</span>
                  <button onClick={() => removeDept(d.id)} className="text-slate-400 hover:text-red-600" data-testid={`remove-dept-${d.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {/* Merge legacy location → active location */}
      <Dialog open={!!mergeSrc} onOpenChange={(v) => !v && setMergeSrc(null)}>
        <DialogContent data-testid="merge-location-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="w-4 h-4 text-[#7C1D3A]" /> Merge location
            </DialogTitle>
            <DialogDescription>
              Every asset stationed at <b className="text-slate-900">{mergeSrc?.name}</b> will be moved to the target
              location. Deployment lines and asset history are updated too.
              {mergeSrc?.orphan
                ? " This is a legacy value found on assets — no master row exists for it, so nothing else needs cleanup."
                : " The source location is deactivated after the merge — this cannot be undone from the UI, but no history is lost."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">Move all assets to</Label>
              <Select value={mergeTarget} onValueChange={setMergeTarget}>
                <SelectTrigger data-testid="merge-target-select">
                  <SelectValue placeholder="Pick a target location" />
                </SelectTrigger>
                <SelectContent>
                  {locations
                    .filter((l) => l.active !== false && (mergeSrc?.orphan || l.id !== mergeSrc?.id))
                    .map((l) => (
                      <SelectItem key={l.id} value={l.name} data-testid={`merge-target-${l.name}`}>
                        {l.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeSrc(null)} data-testid="merge-cancel">
              Cancel
            </Button>
            <Button
              onClick={runMerge}
              disabled={!mergeTarget || merging}
              className="bg-[#7C1D3A] hover:bg-[#6c1c36]"
              data-testid="merge-confirm"
            >
              <GitMerge className="w-4 h-4 mr-2" />
              {merging ? "Merging…" : `Merge into "${mergeTarget || "…"}"`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
